/**
 * Theme behavior: mobile menu, cart drawer, add-to-cart, variant selection.
 * Vanilla custom elements, no build step.
 */

function formatMoney(cents) {
  return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character]);
}

function themeRoute(path) {
  const configuredRoot = window.Shopify?.routes?.root || '/';
  const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
  return `${root}${String(path).replace(/^\/+/, '')}`;
}

async function addToCart({ id, quantity }) {
  // /cart/add.js with a JSON content type requires the {items: [...]} shape;
  // a flat {id, quantity} JSON body is rejected.
  const response = await fetch(themeRoute('cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) || 1 }] }),
  });
  const data = await response.json();
  if (!response.ok || data.status) throw new Error(data.description || data.message);

  const cartResponse = await fetch(themeRoute('cart.js'));
  const cart = await cartResponse.json();
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  return cart;
}

/* -------------------------------------------------------------------------
 * Mobile menu
 * ---------------------------------------------------------------------- */
class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = document.querySelector(`[aria-controls="${this.id}"]`);
    this.toggle?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }
}
customElements.define('mobile-menu', MobileMenu);

/* -------------------------------------------------------------------------
 * Cart drawer
 * ---------------------------------------------------------------------- */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    this.querySelector('.cart-drawer__backdrop')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
    document.addEventListener('cart:updated', (event) => this.render(event.detail.cart));

    document.querySelectorAll('[data-cart-open]').forEach((button) =>
      button.addEventListener('click', (event) => {
        if (button.tagName === 'A') event.preventDefault();
        this.open();
      })
    );

    this.addEventListener('submit', (event) => {
      const discountForm = event.target.closest('form[data-discount-form]');
      if (discountForm) {
        event.preventDefault();
        const code = new FormData(discountForm).get('discount')?.toString().trim();
        if (code) {
          const discountUrl = themeRoute(`discount/${encodeURIComponent(code)}`);
          window.location.assign(`${discountUrl}?redirect=${encodeURIComponent(themeRoute('cart'))}`);
        }
        return;
      }
      const form = event.target.closest('form[data-cart-update]');
      if (!form) return;
      event.preventDefault();
      const formData = new FormData(form);
      this.updateLine(formData.get('id'), formData.get('quantity'));
    });

    // The +/- steppers fire change, not submit (the line forms have no
    // submit button), so quantity edits flow through here.
    this.addEventListener('change', (event) => {
      const form = event.target.closest('form[data-cart-update]');
      if (!form) return;
      const formData = new FormData(form);
      this.updateLine(formData.get('id'), formData.get('quantity'));
    });

    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cart-remove]');
      if (!removeButton) return;
      event.preventDefault();
      this.updateLine(removeButton.dataset.cartRemove, 0);
    });

    // Populate "complete your outfit" for the server-rendered initial state.
    fetch(themeRoute('cart.js'))
      .then((r) => r.json())
      .then((cart) => { this.renderShippingBar(cart); this.renderUpsell(cart); })
      .catch(() => {});

    this.applyDesignPreviews();
  }

  // Swap in stored design renders on the server-rendered markup.
  applyDesignPreviews() {
    this.querySelectorAll('[data-design-id]').forEach((media) => {
      const preview = window.themeDesignPreviews?.get(media.dataset.designId);
      const img = media.querySelector('img');
      if (preview && img) img.src = preview;
    });
  }

  open() {
    this.querySelector('.cart-drawer__body')?.scrollTo({ top: 0, behavior: 'instant' });
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }

  updateLine(id, quantity) {
    this.updateQueue = (this.updateQueue || Promise.resolve()).then(() => this.performUpdateLine(id, quantity));
    return this.updateQueue;
  }

  async performUpdateLine(id, quantity) {
    this.showError('');
    try {
      const response = await fetch(themeRoute('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity: Number(quantity) }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || window.themeStrings.genericError);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      this.showError(error.message || window.themeStrings.genericError);
    }
  }

  showError(message) {
    const errorEl = this.querySelector('[data-cart-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  render(cart) {
    this.showError('');
    const itemsEl = this.querySelector('[data-cart-items]');
    const footerEl = this.querySelector('[data-cart-footer]');
    const headingCount = this.querySelector('[data-cart-heading-count]');
    if (headingCount) headingCount.textContent = cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    this.renderShippingBar(cart);

    const estimatedTotal = this.querySelector('[data-estimated-total]');
    if (estimatedTotal) estimatedTotal.textContent = formatMoney(cart.total_price);

    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `<div class="cart-empty">
        <p>${window.themeStrings.cartEmpty}</p>
        <a href="${themeRoute('collections/all')}" class="button button--secondary" data-close>${window.themeStrings.cartEmptyLink}</a>
      </div>`;
      if (footerEl) footerEl.hidden = true;
      const upsell = this.querySelector('[data-cart-upsell]');
      if (upsell) upsell.hidden = true;
      return;
    }

    if (footerEl) footerEl.hidden = false;

    const deleteIcon = this.querySelector('template[data-icon-delete]')?.innerHTML || window.themeStrings.cartRemove;

    itemsEl.innerHTML = cart.items
      .map((item) => {
        const options = (item.options_with_values || [])
          .filter((o) => o.value !== 'Default Title')
          .map((o) => escapeHtml(o.value))
          .join(' / ');
        const properties = Object.entries(item.properties || {})
          .filter(([key, value]) => value && !key.startsWith('_') && !['Design preview', 'Front artwork', 'Back artwork'].includes(key))
          .map(([, value]) => `<div class="cart-item__variant">${escapeHtml(value)}</div>`)
          .join('');
        // A studio design shows the shopper's own render instead of the
        // stock product photo.
        const designPreview = window.themeDesignPreviews?.get(item.properties?._design_id);
        const proof = /^https:\/\/cdn\.shopify\.com\/.*\/uploads\//.test(item.properties?.['Design preview'] || '') ? item.properties['Design preview'] : null;
        const image = proof || designPreview || item.image;
        return `
      <div class="cart-item">
        <a href="${item.url}" class="cart-item__media">
          ${image ? `<img src="${image}" alt="${escapeHtml(item.product_title)}" width="120" height="150" loading="lazy">` : ''}
        </a>
        <div class="cart-item__info">
          <div class="cart-item__heading">
            <a href="${item.url}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
          </div>
          ${options ? `<div class="cart-item__variant">${options}</div>` : ''}
          ${properties}
          ${proof ? `<a href="${escapeHtml(proof)}" target="_blank" rel="noopener" class="link-underline">${escapeHtml(window.themeStrings.designPreview)}</a>` : ''}
          ${item.original_line_price > item.final_line_price ? `<div class="cart-item__discount-price">${formatMoney(item.final_line_price)} <s>${formatMoney(item.original_line_price)}</s></div>` : ''}
          <div class="cart-item__controls">
            <form data-cart-update>
              <input type="hidden" name="id" value="${item.key}">
              <quantity-input>
                <div class="quantity-selector">
                  <button type="button" data-step="-1" aria-label="Decrease quantity">−</button>
                  <input type="number" name="quantity" value="${item.quantity}" min="0" aria-label="Quantity">
                  <button type="button" data-step="1" aria-label="Increase quantity">+</button>
                </div>
              </quantity-input>
            </form>
            <button type="button" class="cart-item__remove" data-cart-remove="${item.key}" aria-label="${window.themeStrings.cartRemove}">${deleteIcon}</button>
          </div>
        </div>
      </div>`;
      })
      .join('');

    this.renderUpsell(cart);
  }

  renderShippingBar(cart) {
    const bar = this.querySelector('[data-shipping-bar]');
    if (!bar) return;
    const threshold = Number(bar.dataset.threshold);
    if (!threshold) return;
    const remaining = threshold - cart.total_price;
    const textEl = bar.querySelector('[data-shipping-text]');
    const fillEl = bar.querySelector('[data-shipping-fill]');
    if (textEl) {
      textEl.innerHTML = remaining > 0
        ? window.themeStrings.freeShippingProgress.replace('[amount]', formatMoney(remaining))
        : window.themeStrings.freeShippingUnlocked;
    }
    const progress = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100));
    if (fillEl) fillEl.style.width = `${progress}%`;
    bar.querySelector('[role="progressbar"]')?.setAttribute('aria-valuenow', Math.round(progress));
  }

  async renderUpsell(cart) {
    const upsell = this.querySelector('[data-cart-upsell]');
    const row = this.querySelector('[data-cart-upsell-items]');
    const request = this.upsellRequest = (this.upsellRequest || 0) + 1;
    if (!upsell || !row) return;
    upsell.hidden = true;
    row.replaceChildren();
    if (!cart.items?.length) return;
    let catalog = [];
    try { catalog = JSON.parse(this.querySelector('[data-cart-catalog]')?.textContent || '[]'); } catch {}
    let recommended = [];
    try {
      const response = await fetch(`${themeRoute('recommendations/products.json')}?product_id=${cart.items[0].product_id}&limit=10&intent=complementary`);
      if (response.ok) recommended = (await response.json()).products || [];
    } catch {}
    if (request !== this.upsellRequest) return;
    const threshold = Number(this.querySelector('[data-shipping-bar]')?.dataset.threshold || 0);
    const remaining = Math.max(0, threshold - cart.total_price);
    const inCart = new Set(cart.items.map((item) => String(item.product_id)));
    const unique = new Map([...catalog, ...recommended].map((product) => [String(product.id), product]));
    const picks = [...unique.values()].filter((product) =>
      !inCart.has(String(product.id)) && product.available !== false && product.variants?.some((variant) => variant.available)
    ).sort((a, b) => {
      const accessory = (product) => /beanie|hat|cap|sock|accessor/i.test(product.title) ? 0 : 1;
      const gap = (product) => Math.abs(Number(product.price) - remaining);
      return accessory(a) - accessory(b) || (remaining ? gap(a) - gap(b) : a.price - b.price);
    }).slice(0, 3);
    row.innerHTML = picks.map((product) => {
      const variant = product.variants.find((variant) => variant.available);
      const url = product.url || themeRoute(`products/${product.handle}`);
      const image = typeof product.featured_image === 'object' ? product.featured_image?.src : product.featured_image;
      return `<div class="cart-upsell__item">
        <a href="${escapeHtml(url)}" class="cart-upsell__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="80" height="100" loading="lazy">` : ''}</a>
        <div class="cart-upsell__details"><a href="${escapeHtml(url)}" class="cart-upsell__title">${escapeHtml(product.title)}</a><div class="cart-upsell__price">${formatMoney(variant.price)}</div></div>
        <button type="button" class="button button--small" data-quick-add data-variant-id="${variant.id}" ${product.variants.length > 1 ? `data-product-url="${escapeHtml(url)}"` : ''}>${escapeHtml(window.themeStrings.addLabel)} +</button>
      </div>`;
    }).join('');
    upsell.hidden = picks.length === 0;
  }
}
customElements.define('cart-drawer', CartDrawer);

/* -------------------------------------------------------------------------
 * Quantity input (+/- buttons)
 * ---------------------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;
      const step = Number(button.dataset.step);
      const min = Number(this.input.min || 0);
      const next = Math.max(min, Number(this.input.value || 0) + step);
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* -------------------------------------------------------------------------
 * Product form: add to cart via AJAX
 * ---------------------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.productFormReady === 'true') return;
    this.dataset.productFormReady = 'true';
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-add-to-cart-button]') || this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.form || this.submitButton?.disabled || this.getAttribute('aria-busy') === 'true') return;

    const submitText = this.submitButton?.querySelector('[data-add-to-cart-text]');
    const initialText = submitText?.textContent;
    const restoreButton = () => {
      this.removeAttribute('aria-busy');
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
      }
      if (submitText && initialText) submitText.textContent = initialText;
    };

    this.setAttribute('aria-busy', 'true');
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }
    if (submitText) submitText.textContent = window.themeStrings.adding;
    this.showError('');
    let itemWasAdded = false;

    try {
      // Post the form's own fields as form data - the encoding /cart/add
      // has accepted forever - rather than reshaping into JSON.
      const response = await fetch(themeRoute('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const cartError = new Error(data.description || data.message || window.themeStrings.genericError);
        cartError.isCartRejection = response.status >= 400 && response.status < 500;
        throw cartError;
      }
      itemWasAdded = true;

      const cartResponse = await fetch(themeRoute('cart.js'));
      if (!cartResponse.ok) throw new Error(window.themeStrings.genericError);
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      if (submitText) submitText.textContent = window.themeStrings.added;
      this.closest('quick-add-modal')?.close();
      document.querySelector('cart-drawer')?.open();
      window.setTimeout(restoreButton, 900);
    } catch (error) {
      this.showError(error.message || window.themeStrings.genericError);
      restoreButton();

      if (error.isCartRejection) return;
      if (itemWasAdded) {
        window.location.assign(themeRoute('cart'));
        return;
      }

      // If the asynchronous path is unavailable, use the native Shopify form
      // action so the button still adds the selected variant server-side.
      console.error('[theme] AJAX add-to-cart failed, falling back to native submit:', error);
      this.form.submit();
    }
  }

  showError(message) {
    const errorEl = this.querySelector('[data-form-error]');
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }
}
customElements.define('product-form', ProductForm);

/* -------------------------------------------------------------------------
 * Variant picker: swap selected variant, price, media, availability
 * ---------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-variant-json]').textContent);
    } catch (error) {
      console.error('[theme] variant JSON failed to parse:', error);
      this.variants = [];
    }
    this.form = this.closest('product-form')?.querySelector('form') || this.closest('form');
    this.addEventListener('change', () => this.onChange());
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-index]'))
      .map((fieldset) => {
        const checked = fieldset.querySelector('input:checked, select');
        return checked ? checked.value : null;
      });
  }

  onChange() {
    const selected = this.getSelectedOptions();
    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
    const scope = this.closest('[data-quick-add-product], .product-page') || document;

    this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
      fieldset.querySelectorAll('input, option').forEach((input) => {
        const label = fieldset.querySelector(`[data-value="${input.value}"]`) || input;
        label.setAttribute?.('aria-current', input.checked ? 'true' : 'false');
      });
    });

    const notifyButton = scope.querySelector('[data-restock-notify]');
    if (notifyButton) notifyButton.hidden = !variant || variant.available;
    const acceleratedCheckout = scope.querySelector('.quick-add__accelerated-checkout');
    if (acceleratedCheckout) acceleratedCheckout.hidden = !variant?.available;

    if (!variant) {
      const submitButton = this.form?.querySelector('[data-add-to-cart-button]');
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', 'true');
        const text = submitButton.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = window.themeStrings.soldOut;
      }
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput) idInput.value = '';
      return;
    }

    const idInput = this.form?.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const priceEl = scope.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.innerHTML = variant.compare_at_price > variant.price
        ? `<span class="price--sale">${formatMoney(variant.price)}</span><span class="price__compare">${formatMoney(variant.compare_at_price)}</span>`
        : `<span>${formatMoney(variant.price)}</span>`;
    }

    const submitButton = this.form?.querySelector('[type="submit"]');
    if (submitButton) {
      const submitText = submitButton.querySelector('[data-add-to-cart-text]');
      if (submitText) submitText.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
      else submitButton.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
      submitButton.toggleAttribute('aria-disabled', !variant.available);
      submitButton.toggleAttribute('disabled', !variant.available);
    }

    const stockBadge = scope.querySelector('[data-stock-badge]');
    if (stockBadge) {
      const threshold = Number(stockBadge.dataset.threshold || 0);
      const low = threshold > 0
        && variant.inventory_management === 'shopify'
        && variant.inventory_policy === 'deny'
        && variant.inventory_quantity > 0
        && variant.inventory_quantity <= threshold;
      stockBadge.hidden = !low;
      if (low) stockBadge.textContent = window.themeStrings.lowStock.replace('[count]', variant.inventory_quantity);
    }

    const incentive = scope.querySelector('[data-newsletter-incentive]');
    const incentivePrice = incentive?.querySelector('[data-newsletter-price]');
    if (incentive && incentivePrice) {
      const discountPercent = Number(incentive.dataset.discountPercent || 0);
      incentivePrice.textContent = formatMoney(Math.round(variant.price * (100 - discountPercent) / 100));
    }

    if (variant.featured_media) {
      const gallery = scope.querySelector('product-gallery');
      if (gallery) gallery.show(variant.featured_media.preview_image.src, variant.featured_media.alt);
      const mainImage = gallery ? null : scope.querySelector('[data-quick-add-image]');
      if (mainImage) {
        mainImage.src = variant.featured_media.preview_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_800x$1$2');
        mainImage.alt = variant.featured_media.alt || '';
      }
    }

    if (!this.closest('quick-add-modal')) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
}
customElements.define('variant-picker', VariantPicker);

/* -------------------------------------------------------------------------
 * Quick add: product cards open a variant-selection sheet. Buttons without a
 * product URL (such as simple cart recommendations) retain direct-add behavior.
 * ---------------------------------------------------------------------- */
class QuickAddModal extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-quick-add-content]');
    this.querySelectorAll('[data-quick-add-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }

  async open(productUrl, trigger) {
    if (!productUrl || !this.content) return;
    this.trigger = trigger;
    this.content.innerHTML = '<div class="quick-add-modal__loading" role="status">Loading…</div>';
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    try {
      const requestUrl = new URL(productUrl, window.location.origin);
      const response = await fetch(requestUrl.toString());
      if (!response.ok) throw new Error(`Quick add request failed (${response.status})`);
      const responseHtml = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
      const quickAddTemplate = responseDocument.querySelector('[data-quick-add-template]');
      if (!quickAddTemplate) throw new Error('Quick add template missing from product response');
      const fragment = document.importNode(quickAddTemplate.content, true);
      this.content.replaceChildren(fragment);
      customElements.upgrade?.(this.content);
      window.Shopify?.PaymentButton?.init?.();
      this.querySelector('input:checked, button, a')?.focus({ preventScroll: true });
    } catch (error) {
      console.error('[theme] quick add failed to load:', error);
      window.location.assign(productUrl);
    }
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.trigger?.focus?.({ preventScroll: true });
  }
}
customElements.define('quick-add-modal', QuickAddModal);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-quick-add]');
  if (!button || button.hasAttribute('aria-disabled')) return;

  if (button.dataset.productUrl) {
    event.preventDefault();
    const modal = document.querySelector('quick-add-modal');
    if (modal) {
      button.closest('cart-drawer')?.close();
      modal.open(button.dataset.productUrl, button);
    }
    else window.location.assign(button.dataset.productUrl);
    return;
  }

  button.setAttribute('aria-disabled', 'true');
  const originalText = button.textContent;
  button.textContent = window.themeStrings.adding;
  try {
    await addToCart({ id: button.dataset.variantId, quantity: 1 });
    button.textContent = window.themeStrings.added;
    document.querySelector('cart-drawer')?.open();
  } catch (error) {
    console.error('[theme] quick add failed:', error);
    button.textContent = window.themeStrings.genericError;
  } finally {
    window.setTimeout(() => {
      button.removeAttribute('aria-disabled');
      button.textContent = originalText;
    }, 900);
  }
});

/* -------------------------------------------------------------------------
 * Filter tabs: client-side filter of a product grid by product type
 * ---------------------------------------------------------------------- */
class FilterTabs extends HTMLElement {
  connectedCallback() {
    this.grid = this.nextElementSibling;
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('.filter-tab');
      if (!tab) return;
      this.querySelectorAll('.filter-tab').forEach((el) => el.setAttribute('aria-current', 'false'));
      tab.setAttribute('aria-current', 'true');
      this.filter(tab.dataset.filter);
    });
  }

  filter(type) {
    if (!this.grid) return;
    this.grid.querySelectorAll('[data-product-type]').forEach((item) => {
      item.hidden = type !== 'all' && item.dataset.productType !== type;
    });
  }
}
customElements.define('filter-tabs', FilterTabs);

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-collection-layout]');
  if (!button) return;
  const collection = button.closest('.collection--editorial');
  if (!collection) return;
  const single = button.dataset.collectionLayout === 'single';
  collection.classList.toggle('collection--single', single);
  collection.querySelectorAll('[data-collection-layout]').forEach((control) =>
    control.setAttribute('aria-current', control === button ? 'true' : 'false')
  );
});

/* -------------------------------------------------------------------------
 * Product gallery: click a thumbnail to swap the main image
 * ---------------------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.galleryReady) return;
    this.galleryReady = true;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.mainImage = this.querySelector('[data-gallery-main] img');
    this.track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.syncSelection());
    }, { passive: true });
    this.track?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-thumbnail]');
      if (thumb) this.show(thumb.dataset.fullSrc, thumb.dataset.alt);
      else if (event.target.closest('[data-gallery-next]')) this.goTo(this.index + 1);
      else if (event.target.closest('[data-gallery-zoom]')) this.openZoom();
    });
  }

  get index() {
    return this.track?.clientWidth ? Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth) : 0;
  }

  goTo(index) {
    if (!this.slides.length || !this.track) return;
    const next = (index + this.slides.length) % this.slides.length;
    const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: direction * next * this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  syncSelection() {
    const slide = this.slides[this.index];
    if (!slide) return;
    this.mainImage = slide.querySelector('img');
    this.querySelectorAll('[data-thumbnail]').forEach((thumb) => thumb.setAttribute('aria-current', String(thumb.dataset.fullSrc === slide.dataset.fullSrc)));
  }

  show(src, alt) {
    if (!src) return;
    const sourcePath = new URL(src, window.location.origin).pathname;
    let index = this.slides.findIndex((slide) => new URL(slide.dataset.fullSrc, window.location.origin).pathname === sourcePath);
    if (index < 0 && this.slides[0]) {
      index = 0;
      const slide = this.slides[0];
      const image = slide.querySelector('img');
      slide.dataset.fullSrc = src;
      if (image) { image.src = src; image.alt = alt || ''; }
    }
    this.goTo(index);
  }

  openZoom() {
    const dialog = document.querySelector('[data-gallery-zoom-dialog]');
    const image = dialog?.querySelector('[data-gallery-zoom-image]');
    if (!dialog || !image || !this.mainImage) return;
    image.src = this.mainImage.src;
    image.alt = this.mainImage.alt;
    dialog.showModal();
  }
}
customElements.define('product-gallery', ProductGallery);

/* -------------------------------------------------------------------------
 * Generic <dialog> open/close: [data-dialog-open="id"] opens #id,
 * [data-dialog-close] closes its nearest dialog, clicking the backdrop
 * (the dialog element itself, outside its content) closes it too.
 * ---------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.dialogOpen);
    if (dialog) {
      event.preventDefault();
      opener.closest('quick-add-modal')?.close();
      dialog.showModal();
    }
    return;
  }
  if (event.target.closest('[data-dialog-close]')) {
    event.target.closest('dialog')?.close();
    return;
  }
  if (event.target.tagName === 'DIALOG') {
    event.target.close();
  }
});

/* -------------------------------------------------------------------------
 * Sticky add-to-cart bar: mirrors the main product form's submit button
 * ---------------------------------------------------------------------- */
(function stickyAddToCart() {
  const bar = document.querySelector('[data-sticky-add-to-cart]');
  const mainButton = document.querySelector('[data-add-to-cart-button]');
  if (!bar || !mainButton) return;

  bar.hidden = false;

  bar.querySelector('[data-sticky-add-to-cart-button]')?.addEventListener('click', () => {
    const form = mainButton.closest('form');
    // requestSubmit is missing on older iOS Safari; clicking the real submit
    // button fires the same submit event there.
    if (form?.requestSubmit) form.requestSubmit();
    else mainButton.click();
  });

  const observer = new IntersectionObserver(
    ([entry]) => {
      bar.classList.toggle('sticky-add-to-cart--visible', !entry.isIntersecting);
    },
    { threshold: 0 }
  );
  observer.observe(mainButton);
})();

/* -------------------------------------------------------------------------
 * Design previews: thumbnails of studio designs, keyed by the _design_id
 * line item property, so the cart can show what the shopper actually made.
 * (The full-size preview goes to the order itself when an upload target
 * is configured in theme settings.)
 * ---------------------------------------------------------------------- */
window.themeDesignPreviews = {
  key: 'theme:design-previews',
  max: 24,

  all() {
    try {
      return JSON.parse(localStorage.getItem(this.key) || '{}');
    } catch (error) {
      return {};
    }
  },

  put(id, dataUrl) {
    if (!id || !dataUrl) return;
    try {
      const store = this.all();
      store[id] = dataUrl;
      const keys = Object.keys(store);
      // Keep the store bounded — data URLs are heavy.
      for (const stale of keys.slice(0, Math.max(0, keys.length - this.max))) delete store[stale];
      localStorage.setItem(this.key, JSON.stringify(store));
    } catch (error) {
      // Quota exceeded or storage unavailable — the cart just falls back
      // to the product photo.
    }
  },

  get(id) {
    return id ? this.all()[id] || null : null;
  },
};

/* -------------------------------------------------------------------------
 * Recently viewed: records visited products to localStorage
 * ---------------------------------------------------------------------- */
window.themeRecentlyViewed = {
  key: 'theme:recently-viewed',
  max: 12,

  record(product) {
    if (!product?.id) return;
    try {
      const items = this.list().filter((item) => item.id !== product.id);
      items.unshift(product);
      localStorage.setItem(this.key, JSON.stringify(items.slice(0, this.max)));
    } catch (error) {
      // localStorage unavailable (private mode, etc.) - skip silently.
    }
  },

  list(excludeId) {
    try {
      const items = JSON.parse(localStorage.getItem(this.key) || '[]');
      return excludeId ? items.filter((item) => item.id !== excludeId) : items;
    } catch (error) {
      return [];
    }
  },
};

/* -------------------------------------------------------------------------
 * Product recommendations: fetch the section re-rendered by Shopify's
 * recommendations endpoint and swap in the populated markup
 * ---------------------------------------------------------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim().length) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    const excludeId = Number(this.dataset.excludeId);
    const seen = new Set();
    const items = window.themeRecentlyViewed.list(excludeId).filter((item) => {
      const key = item.url?.includes('/products/vintage-washed-frayed-hem-hoodie') ? 'vintage-hoodie' : item.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 8);
    if (items.length === 0) return;

    const row = this.querySelector('[data-recently-viewed-items]');
    if (!row) return;

    row.innerHTML = items.map((item) => this.renderCard(item)).join('');
    this.hidden = false;
  }

  renderCard(item) {
    if (item.url?.includes('/products/vintage-washed-frayed-hem-hoodie')) item = { ...item, url: '/products/vintage-washed-frayed-hem-hoodie' };
    const onSale = item.compareAtPrice > item.price;
    const priceHtml = onSale
      ? `<span class="price--sale">${formatMoney(item.price)}</span><span class="price__compare">${formatMoney(item.compareAtPrice)}</span>`
      : `<span>${formatMoney(item.price)}</span>`;

    return `
      <div class="carousel-row__item">
        <div class="product-card">
          <div class="product-card__media">
            <a href="${item.url}" class="product-card__media-link">
              <img src="${item.image}" alt="" width="600" height="750" loading="lazy">
            </a>
          </div>
          <a href="${item.url}" class="product-card__link">
            <div class="product-card__title">${item.title}</div>
            <div class="product-card__price">${priceHtml}</div>
          </a>
        </div>
      </div>`;
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);

/* -------------------------------------------------------------------------
 * Header shadow on scroll (subtle, optional)
 * ---------------------------------------------------------------------- */
(function headerScrollState() {
  const header = document.querySelector('.header');
  if (!header) return;
  let lastScroll = 0;
  document.addEventListener('scroll', () => {
    const scrolled = window.scrollY > 4;
    header.classList.toggle('header--scrolled', scrolled);
    lastScroll = window.scrollY;
  }, { passive: true });
})();

